import { getEnvironment } from 'utils';
import { setupPendoScript } from './pendoScriptUtils';
import { PENDO_ENVIRONMENT_CONFIG } from '../constants';

export const clearPendoSession = () => {
  try {
    if (pendo && pendo.clearSession) {
      pendo.clearSession();
    }
  } catch (error) {
    console.error('Error clearing Pendo session:', error);
  }
};

const shouldInitialize = () => {
  const env = getEnvironment();
  const isInvalidEnv = env === 'local' || env === 'auto' || env === 'dev';
  if (isInvalidEnv) {
    console.warn('Pendo initialization skipped: Invalid environment', env);
  }
  // Ensure Pendo key is set in environment variables
  const hasPendoKey = process.env.REACT_APP_PENDO_KEY !== undefined;
  if (!hasPendoKey) {
    console.warn('Pendo initialization skipped: Missing Pendo key');
  }

  return !isInvalidEnv && hasPendoKey;
};

// Exported for testing purposes
export function getPendoConfig(userId: number, hospitalId: number) {
  const pendoConfig: Record<string, any> = {};
  pendoConfig.visitor = {};
  pendoConfig.account = {};

  if (!process.env.REACT_APP_PENDO_KEY) {
    throw new Error('Pendo Key is missing');
  }
  // If session expires, and apiKey is not available, pendo will not be initialized properly
  // (Case :  If we come to an open tab after long time, and not refresh the script
  //  will not be loaded again to set the apiKey)
  // So, setting the apiKey again here to ensure it's always available
  pendoConfig.apiKey = process.env.REACT_APP_PENDO_KEY;

  let pendoEnv: string = PENDO_ENVIRONMENT_CONFIG[getEnvironment()];

  if (pendoEnv === 'DEV') {
    const personalEnvName = window.location.host.split('.')[0];
    pendoEnv = `DEV-${personalEnvName.toUpperCase()}`;
  }

  pendoConfig.visitor.id = `${pendoEnv}_BROWSER_${userId}`;
  pendoConfig.account.id = `${pendoEnv}_BROWSER_${hospitalId}`;

  return pendoConfig;
}

export async function initializePendo(userId?: number, hospitalId?: number) {
  try {
    if (!shouldInitialize()) {
      return;
    }
    // Check if pendo has been injected in the window object
    // If not, load the pendo script again
    if (!pendo) {
      await setupPendoScript();
    }

    if (!userId || !hospitalId) {
      console.warn('Pendo initialization skipped: Missing userId or hospitalId');
      return;
    }

    const pendoConfig = getPendoConfig(userId, hospitalId);

    if (!!pendo.isReady && pendo.isReady()) {
      // Updated existing pendo instance as it's already initialized
      pendo.identify(pendoConfig);
    } else {
      pendo.initialize(pendoConfig);
    }
  } catch (error) {
    // Not propagating the error to avoid handling error in the component
    console.error('Error initializing Pendo:', error);
  }
}

export const trackEvent = async (eventName: string, eventData: Record<string, any>) => {
  try {
    pendo.track(eventName, eventData);
  } catch (error) {
    console.error('Error tracking event', error);
  }
};
